import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { isEmpty } from 'lodash';
import { Actions, Async, F1 } from '@client/new/helpers/types';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { muiForm } from '@client/new/helpers/forms';
import { z } from 'zod';
import { browserHistory } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Exact,
  GetInfluencerQuery,
  useGetInfluencerQuery,
  useUpdateCreatorProfileMutation,
} from 'generated/graphql';
import { ApolloQueryResult } from '@apollo/client';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';
import { resolveDefaultPhoneCountryDialCode } from '../auth/model/auth.phone-options.model';
import {
  REGISTRATION_PHONE_NUMBER_REGEX,
  buildRegistrationPhoneNumber,
} from '../auth/model/registration.model';

type Creator = GetInfluencerQuery['creator'];
export type Influencer = NonNullable<Creator>;

export type SettingsView = {
  dispatch: SettingsViewDispatcher;
  submitting: boolean;
  register: UseFormRegister<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  watch: UseFormWatch<InitialForm>;
  setValue: UseFormSetValue<InitialForm>;
  refetch: (
    variables?: Partial<Exact<{ id: number }>> | undefined,
  ) => Promise<ApolloQueryResult<GetInfluencerQuery>>;
  influencer: Influencer;
  showFeatureSettings: boolean;
};

export type SettingsViewActionsMap = {
  submit: null;
  cancel: null;
};

export type SettingsViewActions = Actions<SettingsViewActionsMap>;

export type SettingsViewDispatcher = F1<SettingsViewActions>;

export const settingsForm = z.object({
  email: z
    .string()
    .nonempty('Field is required')
    .email('This is not a valid email'),
  phoneCountryDialCode: z.string(),
  phoneNumber: z.string(),
});

export type InitialForm = {
  email: string;
  phoneCountryDialCode: string;
  phoneNumber: string;
};

const toPhoneFormValues = (
  mobilePhoneNumber: string | null | undefined,
): Pick<InitialForm, 'phoneCountryDialCode' | 'phoneNumber'> => {
  const defaultPhoneValues = {
    phoneCountryDialCode: resolveDefaultPhoneCountryDialCode(),
    phoneNumber: '',
  };

  if (!mobilePhoneNumber) {
    return defaultPhoneValues;
  }

  const [phoneCountryDialCode, phoneNumberInput] = mobilePhoneNumber
    .trim()
    .split(' ');
  const phoneNumber = phoneNumberInput?.replace(/\D/g, '') ?? '';
  const parsedPhone = `${phoneCountryDialCode} ${phoneNumber}`;

  if (!REGISTRATION_PHONE_NUMBER_REGEX.test(parsedPhone)) {
    return defaultPhoneValues;
  }

  return {
    phoneCountryDialCode,
    phoneNumber,
  };
};

type SettingsViewModelParams = {
  influencerId: number;
};

export const useSettingsViewModel = ({
  influencerId,
}: SettingsViewModelParams): Async<SettingsView> => {
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, refetch } = useGetInfluencerQuery({
    variables: { id: influencerId },
    fetchPolicy: 'no-cache',
  });

  const [updateCreatorProfileMutation] = useUpdateCreatorProfileMutation();

  const { register, handleSubmit, control, watch, setValue, reset } = muiForm(
    useForm<InitialForm, any, InitialForm>({
      defaultValues: {
        email: '',
        phoneCountryDialCode: resolveDefaultPhoneCountryDialCode(),
        phoneNumber: '',
      },
      resolver: zodResolver(settingsForm),
    }),
  );

  useEffect(() => {
    if (!isEmpty(data?.creator)) {
      reset({
        email: data.creator.email || '',
        ...toPhoneFormValues(data.creator.mobilePhoneNumber),
      });
    }
  }, [data, reset]);

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error || !data?.creator) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const influencer = data?.creator;

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const mobilePhoneNumber = buildRegistrationPhoneNumber(
        values.phoneCountryDialCode,
        values.phoneNumber,
      );
      updateCreatorProfileMutation({
        variables: {
          data: {
            id: influencerId,
            email: values.email,
            mobilePhoneNumber,
          },
        },
      });
      toast.success('Settings updated successfully!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  });

  const dispatch: SettingsViewDispatcher = (action) => {
    switch (action.type) {
      case 'submit':
        onSubmit();
        return null;
      case 'cancel':
        browserHistory.goBack();
        return null;
    }
  };

  const showFeatureSettings =
    influencer.businessModel === 'legacy' &&
    (isSubscriptionCreator(influencer?.offering) ||
      hasPremiumFeaturesOTPIds(influencer?.customizations));

  return {
    type: 'fetched',
    value: {
      dispatch,
      submitting,
      control,
      register,
      watch,
      setValue,
      refetch,
      influencer,
      showFeatureSettings,
    },
  };
};
